import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  Loader2Icon,
  AlertCircleIcon,
  NavigationIcon,
  CrosshairIcon,
  CompassIcon,
  ChevronRightIcon,
  MapPinIcon,
  CameraIcon,
  TrophyIcon,
  SparklesIcon,
  PartyPopperIcon,
  CheckCircle2Icon,
  ListIcon,
  TargetIcon,
} from 'lucide-react';

import {
  getGetHuntQueryOptions,
  getGetMyHuntProgressQueryOptions,
  captureStation,
  ApiError,
} from '@cdf/api-client-react';
import type {
  PublicStation,
  CaptureResult,
  HuntState,
} from '@cdf/api-client-react';

import { useHuntSession, huntAuthHeaders } from '@/hooks/use-hunt-session';
import { Confetti, ChallengeCard } from '@/pages/hunt/checkin-page';
import { useCamera } from '@/hooks/use-camera';
import { useGeolocation } from '@/hooks/use-geolocation';
import { useHeading } from '@/hooks/use-heading';
import { projectAsset, bearingLabel } from '@/lib/ar-projection';
import { formatDistance } from '@/lib/report-helpers';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// ── Geo utilities ──────────────────────────────────────────────────────────

const EARTH_R = 6371000; // metres

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return EARTH_R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function bearingTo(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180) / Math.PI + 360;
}

const GPS_SLACK_M = 15; // match server-side slack

type CapturePhase =
  | { kind: 'idle' }
  | { kind: 'success'; result: CaptureResult }
  | { kind: 'already' }
  | { kind: 'error'; message: string };

type ViewMode = 'onboarding' | 'pseudo-ar' | 'list-fallback';

// ── Main page ──────────────────────────────────────────────────────────────

export function GpsCapturePage() {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.huntSlug ?? '';
  const stationId = params.stationId ?? '';

  const { session } = useHuntSession(slug);

  const huntQuery = useQuery({
    ...getGetHuntQueryOptions(slug),
    enabled: !!slug,
  });

  const progressQuery = useQuery({
    ...getGetMyHuntProgressQueryOptions(slug, {
      request: session ? huntAuthHeaders(session.token) : undefined,
    }),
    enabled: !!slug && !!session,
    refetchInterval: 10_000,
  });

  const [mode, setMode] = React.useState<ViewMode>('onboarding');
  const [phase, setPhase] = React.useState<CapturePhase>({ kind: 'idle' });

  const camera = useCamera();
  const geo = useGeolocation();
  const heading = useHeading();

  const hunt = huntQuery.data;
  const station: PublicStation | undefined = hunt?.stations.find(
    (s) => s.id === stationId && s.kind === 'gps'
  );

  const capturedStationIds = progressQuery.data?.capturedStationIds ?? [];
  const alreadyCaptured = capturedStationIds.includes(stationId);

  // Compute live distance + bearing to station
  const stationGeo = React.useMemo(() => {
    if (!station || station.latitude == null || station.longitude == null) return null;
    return { lat: station.latitude, lng: station.longitude };
  }, [station]);

  const distance = React.useMemo(() => {
    if (!geo.position || !stationGeo) return null;
    return haversine(geo.position.lat, geo.position.lng, stationGeo.lat, stationGeo.lng);
  }, [geo.position, stationGeo]);

  const stationBearing = React.useMemo(() => {
    if (!geo.position || !stationGeo) return null;
    return bearingTo(geo.position.lat, geo.position.lng, stationGeo.lat, stationGeo.lng) % 360;
  }, [geo.position, stationGeo]);

  const captureRadius = (station?.captureRadiusM ?? 50) + GPS_SLACK_M;
  const inRange = distance != null && distance <= captureRadius;

  // Start sensors
  const handleStart = React.useCallback(async () => {
    await Promise.all([camera.start(), geo.start(), heading.start()]);
    setMode('pseudo-ar');
  }, [camera, geo, heading]);

  const handleStartList = React.useCallback(async () => {
    await geo.start();
    setMode('list-fallback');
  }, [geo]);

  // Auto-fallback to list if camera is denied/unsupported
  React.useEffect(() => {
    if (
      mode === 'pseudo-ar' &&
      (camera.state === 'denied' || camera.state === 'unsupported' || camera.state === 'error')
    ) {
      setMode('list-fallback');
    }
  }, [mode, camera.state]);

  // Capture mutation
  const captureMutation = useMutation({
    mutationFn: async () => {
      if (!session) throw new Error('No session');
      if (!geo.position) throw new Error('No GPS position');
      return captureStation(
        slug,
        {
          stationId,
          latitude: geo.position.lat,
          longitude: geo.position.lng,
        },
        huntAuthHeaders(session.token)
      );
    },
    onSuccess: (data) => {
      setPhase({ kind: 'success', result: data });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'ALREADY_CAPTURED') {
          setPhase({ kind: 'already' });
        } else if (err.status === 400) {
          setPhase({
            kind: 'error',
            message: 'You are too far from this station to capture it. Move closer and try again.',
          });
        } else if (err.status === 404) {
          setPhase({ kind: 'error', message: 'Station not found or not a GPS station.' });
        } else {
          setPhase({ kind: 'error', message: err.message });
        }
      } else {
        setPhase({ kind: 'error', message: 'Something went wrong. Please try again.' });
      }
    },
  });

  const handleCapture = React.useCallback(() => {
    if (!inRange || captureMutation.isPending) return;
    captureMutation.mutate();
  }, [inRange, captureMutation]);

  // ── Redirect cases ───────────────────────────────────────────────────────

  if (huntQuery.isLoading) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="mt-4 h-8 w-48" />
      </div>
    );
  }

  if (huntQuery.isError || !hunt) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <AlertCircleIcon className="mb-4 h-10 w-10 text-destructive" />
        <h1 className="text-xl font-bold">Hunt Unavailable</h1>
        <p className="mt-1 text-center text-muted-foreground">
          This hunt could not be loaded. It may not exist or the service is unavailable.
        </p>
        <Button asChild variant="ghost" className="mt-6">
          <Link to="/">
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Home
          </Link>
        </Button>
      </div>
    );
  }

  if (hunt.status !== 'active') {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <TrophyIcon className="mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-bold">Hunt Not Active</h1>
        <p className="mt-1 text-center text-muted-foreground">
          {hunt.name} is not currently active.
        </p>
        <Button asChild variant="ghost" className="mt-6">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Passport
          </Link>
        </Button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <AlertCircleIcon className="mb-4 h-10 w-10 text-amber-500" />
        <h1 className="text-xl font-bold">Join Required</h1>
        <p className="mt-1 text-center text-muted-foreground">
          You need to join the hunt before you can capture stations.
        </p>
        <Button asChild className="mt-6">
          <Link to={`/hunt/${slug}`}>Join Hunt</Link>
        </Button>
      </div>
    );
  }

  if (!station) {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <AlertCircleIcon className="mb-4 h-10 w-10 text-destructive" />
        <h1 className="text-xl font-bold">Station Not Found</h1>
        <p className="mt-1 text-center text-muted-foreground">
          This station doesn't exist or is not a GPS station.
        </p>
        <Button asChild variant="ghost" className="mt-6">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Passport
          </Link>
        </Button>
      </div>
    );
  }

  // ── Already captured (from progress data) ────────────────────────────────
  if (alreadyCaptured && phase.kind === 'idle') {
    return <AlreadyCapturedView station={station} hunt={hunt} slug={slug} />;
  }

  // ── Capture success ──────────────────────────────────────────────────────
  if (phase.kind === 'success') {
    return <CaptureCelebration result={phase.result} hunt={hunt} slug={slug} />;
  }

  // ── Capture error ────────────────────────────────────────────────────────
  if (phase.kind === 'error') {
    return (
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <AlertCircleIcon className="mb-4 h-10 w-10 text-destructive" />
        <h1 className="text-xl font-bold">Capture Failed</h1>
        <p className="mt-1 text-center text-muted-foreground">{phase.message}</p>
        <Button className="mt-6" onClick={() => setPhase({ kind: 'idle' })}>
          Try Again
        </Button>
        <Button asChild variant="ghost" className="mt-2">
          <Link to={`/hunt/${slug}`}>Back to passport</Link>
        </Button>
      </div>
    );
  }

  if (phase.kind === 'already') {
    return <AlreadyCapturedView station={station} hunt={hunt} slug={slug} />;
  }

  // ── Onboarding ───────────────────────────────────────────────────────────
  if (mode === 'onboarding') {
    return (
      <OnboardingView
        stationName={station.name}
        distance={distance}
        onStart={handleStart}
        onStartList={handleStartList}
        onBack={() => navigate(`/hunt/${slug}`)}
      />
    );
  }

  // ── List fallback ────────────────────────────────────────────────────────
  if (mode === 'list-fallback' || (mode === 'pseudo-ar' && camera.state !== 'active')) {
    return (
      <ListFallbackView
        station={station}
        distance={distance}
        stationBearing={stationBearing}
        inRange={inRange}
        captureRadius={captureRadius}
        geoState={geo.state}
        isCapturing={captureMutation.isPending}
        onCapture={handleCapture}
        onBack={() => navigate(`/hunt/${slug}`)}
      />
    );
  }

  // ── Pseudo-AR camera overlay ─────────────────────────────────────────────
  const userHeading = heading.heading ?? 0;

  return (
    <ArCaptureView
      station={station}
      distance={distance}
      stationBearing={stationBearing}
      userHeading={userHeading}
      inRange={inRange}
      captureRadius={captureRadius}
      headingValue={heading.heading}
      headingState={heading.state}
      geoState={geo.state}
      cameraVideoRef={camera.videoRef}
      isCapturing={captureMutation.isPending}
      onCapture={handleCapture}
      onBack={() => navigate(`/hunt/${slug}`)}
    />
  );
}

// ── Onboarding ─────────────────────────────────────────────────────────────

function OnboardingView({
  stationName,
  distance,
  onStart,
  onStartList,
  onBack,
}: {
  stationName: string;
  distance: number | null;
  onStart: () => void;
  onStartList: () => void;
  onBack: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex items-center gap-2 border-b p-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">Capture: {stationName}</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col items-center justify-center gap-6 p-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <TargetIcon className="h-10 w-10 text-primary" />
          </div>

          <div className="max-w-md space-y-3 text-center">
            <h2 className="text-xl font-semibold">Walk to the station</h2>
            <p className="text-sm text-muted-foreground">
              Use the AR camera view or the list view to navigate to{' '}
              <span className="font-medium">{stationName}</span>. When you're within
              range, a Capture button will appear.
            </p>
            {distance != null && (
              <div className="inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1.5">
                <NavigationIcon className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">{formatDistance(distance)}</span>
              </div>
            )}
          </div>

          <div className="w-full max-w-md space-y-3">
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <CameraIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Camera view</p>
                <p className="text-muted-foreground">
                  See the station marker overlaid on your camera feed with live distance.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Location access</p>
                <p className="text-muted-foreground">Required to measure distance to the station.</p>
              </div>
            </div>
          </div>

          <div className="flex w-full max-w-md flex-col gap-2">
            <Button size="lg" onClick={onStart}>
              <CameraIcon className="mr-2 h-5 w-5" />
              Start AR View
            </Button>
            <Button variant="outline" onClick={onStartList}>
              <ListIcon className="mr-2 h-4 w-4" />
              List Only (no camera)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            No camera? The list view shows distance and capture without sensors.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── AR camera overlay ──────────────────────────────────────────────────────

function ArCaptureView({
  station,
  distance,
  stationBearing,
  userHeading,
  inRange,
  captureRadius,
  headingValue,
  headingState,
  geoState,
  cameraVideoRef,
  isCapturing,
  onCapture,
  onBack,
}: {
  station: PublicStation;
  distance: number | null;
  stationBearing: number | null;
  userHeading: number;
  inRange: boolean;
  captureRadius: number;
  headingValue: number | null;
  headingState: string;
  geoState: string;
  cameraVideoRef: React.RefCallback<HTMLVideoElement>;
  isCapturing: boolean;
  onCapture: () => void;
  onBack: () => void;
}) {
  const projected =
    stationBearing != null
      ? projectAsset(stationBearing, userHeading, distance ?? 999)
      : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black">
      <video
        ref={cameraVideoRef}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        playsInline
        muted
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50" />

      {/* Top bar */}
      <div className="absolute left-0 right-0 top-0 z-20 flex items-center gap-2 bg-black/50 p-3 backdrop-blur-sm">
        <Button variant="ghost" size="icon" className="text-white" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 text-white">
          <CompassIcon className="h-4 w-4" />
          <span className="text-sm font-medium">
            {headingValue != null
              ? `${Math.round(headingValue)}° ${bearingLabel(headingValue)}`
              : 'No compass'}
          </span>
        </div>
        {geoState === 'active' && (
          <Badge variant="secondary" className="ml-auto bg-white/20 text-white">
            <CrosshairIcon className="mr-1 h-3 w-3" />
            GPS
          </Badge>
        )}
      </div>

      {/* Heading permission/unsupported banner */}
      {(headingState === 'denied' || headingState === 'unsupported') && (
        <div className="absolute left-0 right-0 top-14 z-20 flex items-center gap-2 bg-amber-500/90 p-2 text-black">
          <AlertCircleIcon className="h-4 w-4 shrink-0" />
          <span className="text-xs">
            {headingState === 'denied'
              ? 'Motion permission denied. Enable Motion & Orientation in browser settings for compass alignment.'
              : 'Compass not available on this device. Direction indicator is approximate.'}
          </span>
        </div>
      )}

      {/* Compass strip — guide toward offscreen station */}
      {projected && !projected.inView && (
        <div className="absolute left-0 right-0 top-14 z-15 flex h-10 items-center justify-center">
          <div className="flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 backdrop-blur-sm">
            <ChevronRightIcon
              className={cn('h-4 w-4 text-white', projected.offscreenDirection === -1 && 'rotate-180')}
            />
            <span className="text-xs font-medium text-white">
              {formatDistance(distance ?? undefined)} · {bearingLabel(stationBearing ?? 0)}
            </span>
            <ChevronRightIcon
              className={cn('h-4 w-4 text-white', projected.offscreenDirection === -1 && 'rotate-180')}
            />
          </div>
        </div>
      )}

      {/* Station marker — in view */}
      {projected && projected.inView && (
        <div className="absolute inset-0 z-10">
          <div
            className="absolute"
            style={{
              left: `${(projected.screenX ?? 0.5) * 100}%`,
              top: `${projected.screenY * 100}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <StationMarker station={station} distance={distance} captured={false} />
          </div>
        </div>
      )}

      {/* Waiting for GPS */}
      {distance == null && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-white">
            <Loader2Icon className="h-6 w-6 animate-spin" />
            <p className="text-sm">Waiting for GPS…</p>
          </div>
        </div>
      )}

      {/* Bottom capture bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/60 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur-sm">
        {distance != null && (
          <div className="mb-3 flex items-center justify-center gap-3">
            <div
              className={cn(
                'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium',
                inRange ? 'bg-green-500 text-black' : 'bg-white/20 text-white'
              )}
            >
              <NavigationIcon className="h-4 w-4" />
              {formatDistance(distance)}
            </div>
            {inRange ? (
              <Badge className="bg-green-600 hover:bg-green-600">
                <SparklesIcon className="mr-1 h-3 w-3" />
                In range!
              </Badge>
            ) : (
              <span className="text-xs text-white/60">
                Need to be within {Math.round(captureRadius - GPS_SLACK_M)}m
              </span>
            )}
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          disabled={!inRange || isCapturing}
          onClick={onCapture}
        >
          {isCapturing ? (
            <>
              <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
              Capturing…
            </>
          ) : inRange ? (
            <>
              <TargetIcon className="mr-2 h-5 w-5" />
              Capture Station (+{station.points} pts)
            </>
          ) : (
            <>
              <MapPinIcon className="mr-2 h-5 w-5" />
              Move closer to capture
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ── List fallback view ─────────────────────────────────────────────────────

function ListFallbackView({
  station,
  distance,
  stationBearing,
  inRange,
  captureRadius,
  geoState,
  isCapturing,
  onCapture,
  onBack,
}: {
  station: PublicStation;
  distance: number | null;
  stationBearing: number | null;
  inRange: boolean;
  captureRadius: number;
  geoState: string;
  isCapturing: boolean;
  onCapture: () => void;
  onBack: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex items-center gap-2 border-b p-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeftIcon className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold">Capture: {station.name}</h1>
        {geoState === 'active' && (
          <Badge variant="secondary" className="ml-auto">
            <CrosshairIcon className="mr-1 h-3 w-3" />
            GPS Active
          </Badge>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* GPS states */}
        {geoState === 'requesting' && (
          <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
            <Loader2Icon className="h-6 w-6 animate-spin" />
            <p className="text-sm">Getting your location…</p>
          </div>
        )}

        {geoState === 'denied' && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <AlertCircleIcon className="h-10 w-10 text-amber-500" />
            <p className="text-sm font-medium">Location permission needed</p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Allow location access so we can measure your distance to the station.
            </p>
          </div>
        )}

        {geoState === 'active' && (
          <div className="space-y-4">
            {/* Distance card */}
            <Card
              className={cn(
                'border-2',
                inRange ? 'border-green-500 bg-green-500/5' : 'border-border'
              )}
            >
              <CardContent className="flex flex-col items-center gap-3 py-6">
                <div
                  className={cn(
                    'flex h-16 w-16 items-center justify-center rounded-full',
                    inRange ? 'bg-green-500/10' : 'bg-muted'
                  )}
                >
                  <TargetIcon
                    className={cn(
                      'h-8 w-8',
                      inRange ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                    )}
                  />
                </div>

                {distance != null ? (
                  <>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{Math.round(distance)}m</p>
                      <p className="text-sm text-muted-foreground">from {station.name}</p>
                    </div>
                    {stationBearing != null && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <NavigationIcon className="h-4 w-4 text-blue-500" />
                        Head {bearingLabel(stationBearing)}
                      </div>
                    )}
                    {inRange ? (
                      <Badge className="bg-green-600 hover:bg-green-600">
                        <SparklesIcon className="mr-1 h-3 w-3" />
                        In range — capture now!
                      </Badge>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Move {Math.round(distance - (captureRadius - GPS_SLACK_M))}m closer to
                        capture (within {Math.round(captureRadius - GPS_SLACK_M)}m)
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Computing distance…</p>
                )}
              </CardContent>
            </Card>

            {/* Station info */}
            <Card>
              <CardContent className="flex items-center gap-3 py-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                  <NavigationIcon className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{station.name}</p>
                  <p className="text-xs text-muted-foreground">
                    GPS Station · {station.points} pts
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Capture button */}
            <Button
              className="w-full"
              size="lg"
              disabled={!inRange || isCapturing}
              onClick={onCapture}
            >
              {isCapturing ? (
                <>
                  <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                  Capturing…
                </>
              ) : inRange ? (
                <>
                  <TargetIcon className="mr-2 h-5 w-5" />
                  Capture Station (+{station.points} pts)
                </>
              ) : (
                <>
                  <MapPinIcon className="mr-2 h-5 w-5" />
                  Move closer to capture
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Station marker (AR overlay) ────────────────────────────────────────────

function StationMarker({
  station,
  distance,
  captured,
}: {
  station: PublicStation;
  distance: number | null;
  captured: boolean;
}) {
  return (
    <div
      className={cn(
        'flex cursor-pointer flex-col items-center gap-1 rounded-xl border-2 px-4 py-3 backdrop-blur-sm transition-colors',
        captured
          ? 'border-muted bg-black/40'
          : 'border-amber-400 bg-black/60'
      )}
    >
      <TargetIcon
        className={cn(
          'h-8 w-8',
          captured ? 'text-muted-foreground' : 'text-amber-400'
        )}
      />
      <span className="text-sm font-semibold text-white">{station.name}</span>
      {distance != null && (
        <span className="text-xs text-white/70">{formatDistance(distance)}</span>
      )}
      <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-black">
        +{station.points} pts
      </span>
    </div>
  );
}

// ── Capture celebration (reuses shared Confetti + ChallengeCard) ──────────

function CaptureCelebration({
  result,
  hunt,
  slug,
}: {
  result: CaptureResult;
  hunt: HuntState;
  slug: string;
}) {
  const challenges = hunt.challenges.filter((c) => c.stationId === result.stationId);

  return (
    <>
      <Confetti />
      <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-green-500/10">
            <PartyPopperIcon className="h-12 w-12 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Station Captured!</h1>
          <p className="mt-1 text-lg font-medium">{result.stationName}</p>
          <div className="mt-3 flex items-center gap-2">
            <Badge className="bg-green-600 hover:bg-green-600">
              <SparklesIcon className="mr-1 h-3 w-3" />
              +{result.pointsAwarded} points
            </Badge>
            <Badge variant="outline">
              <TrophyIcon className="mr-1 h-3 w-3" />
              {result.totalPoints} total
            </Badge>
          </div>
        </div>

        {challenges.length > 0 && (
          <div className="w-full space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Complete the challenge to earn more points!
            </p>
            {challenges.map((ch) => (
              <ChallengeCard key={ch.id} challenge={ch} slug={slug} />
            ))}
          </div>
        )}

        <Button asChild variant="ghost" className="mt-6">
          <Link to={`/hunt/${slug}`}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to passport
          </Link>
        </Button>
      </div>
    </>
  );
}

// ── Already captured view ─────────────────────────────────────────────────

function AlreadyCapturedView({
  station,
  hunt,
  slug,
}: {
  station: PublicStation;
  hunt: HuntState;
  slug: string;
}) {
  const challenges = hunt.challenges.filter((c) => c.stationId === station.id);

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col items-center justify-center p-6">
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-500/10">
          <CheckCircle2Icon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-bold tracking-tight">Already Captured</h1>
        <p className="mt-1 text-muted-foreground">
          You've already captured <span className="font-medium">{station.name}</span>.
        </p>
      </div>

      {challenges.length > 0 && (
        <div className="w-full space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            Challenges for this station:
          </p>
          {challenges.map((ch) => (
            <ChallengeCard key={ch.id} challenge={ch} slug={slug} />
          ))}
        </div>
      )}

      <Button asChild variant="ghost" className="mt-6">
        <Link to={`/hunt/${slug}`}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to passport
        </Link>
      </Button>
    </div>
  );
}
